import { useState, type FormEvent } from 'react';
import { api } from '../api/client.js';
import type { SessionUser } from '../api/client.js';
import { SiteFooter } from '../components/SiteFooter.js';

/**
 * หน้าเข้าสู่ระบบ
 *
 * ตัวเซสชันอยู่ใน cookie แบบ httpOnly ที่เซิร์ฟเวอร์ตั้งให้ หน้านี้จึงไม่ได้เก็บ token อะไรไว้เอง
 * และไม่มีการเขียนอะไรลง localStorage เลย ปิดเบราว์เซอร์แล้วเซสชันยังอยู่จนกว่าจะหมดอายุ 8 ชั่วโมง
 *
 * ข้อความตอนเข้าไม่สำเร็จใช้ข้อความเดียวกับที่เซิร์ฟเวอร์ส่งมา ซึ่งจงใจไม่บอกว่า
 * "ไม่มีชื่อผู้ใช้นี้" หรือ "รหัสผ่านผิด" เพราะการแยกสองอย่างนี้เท่ากับบอกใบ้ว่าบัญชีไหนมีอยู่จริง
 */
export function Login({ onSuccess }: { onSuccess: (user: SessionUser) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await api.login(username, password);
      onSuccess(user);
    } catch (err) {
      setError(messageOf(err));
      setPassword('');
      setShowPassword(false);
      setBusy(false);
    }
  }

  return (
    <div className="login">
      {/* ฝั่งซ้าย: อัตลักษณ์หน่วยงาน — จอแคบจะซ่อนไป เหลือแค่ฟอร์ม */}
      <aside className="login-side">
        <OrgLattice />
        <div className="login-side-in">
          <div className="login-mark">
            <span className="dot" aria-hidden="true" />
            <b>YANA</b>
          </div>
          <h1>
            วางแผนกำลังคนล่วงหน้า
            <br />
            จากข้อมูลจริง
          </h1>
          <p>
            ดูล่วงหน้าว่าหน่วยงานใดจะมีผู้เกษียณ ตำแหน่งใดยังไม่มีผู้พร้อมรับช่วง
            และการเลื่อนที่ผ่านมาเป็นอย่างไร
          </p>
          <span className="login-org">สำนักงานปลัดกระทรวงการคลัง</span>
        </div>
      </aside>

      {/* ฝั่งขวา: ฟอร์ม — footer เกาะอยู่ล่างสุดของฝั่งนี้ ไม่ทับแผงอัตลักษณ์ทางซ้าย */}
      <main className="login-main">
        <form className="login-form" onSubmit={submit}>
          {/* จอแคบไม่มีแผงซ้าย ต้องบอกให้ได้ว่านี่คือระบบอะไรของใคร ไม่ใช่ฟอร์มลอย ๆ */}
          <div className="login-brand-sm">
            <span className="dot" aria-hidden="true" />
            <b>YANA</b>
            <span className="org">ฐานข้อมูลบุคลากร · สำนักงานปลัดกระทรวงการคลัง</span>
          </div>

          <div className="login-head">
            <h2>เข้าสู่ระบบ</h2>
            <p>ใช้บัญชีที่หน่วยงานออกให้</p>
          </div>

          <label className="field">
            <span>ชื่อผู้ใช้</span>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              disabled={busy}
              placeholder="เช่น somchai"
            />
          </label>

          <label className="field">
            <span>รหัสผ่าน</span>
            <div className="field-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={busy}
              />
              {/* ให้ผู้ใช้ตรวจสิ่งที่พิมพ์ได้เอง ลดการพิมพ์ผิดซ้ำ ๆ จนโดนระบบล็อก */}
              <button
                type="button"
                className="peek"
                onClick={() => setShowPassword((v) => !v)}
                aria-pressed={showPassword}
                aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                disabled={busy}
              >
                {showPassword ? 'ซ่อน' : 'ดู'}
              </button>
            </div>
          </label>

          {/* role=alert เพื่อให้โปรแกรมอ่านหน้าจออ่านข้อความนี้ทันทีที่ขึ้น */}
          {error && (
            <p className="login-error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="login-btn" disabled={busy || !username || !password}>
            {busy ? 'กำลังตรวจสอบ…' : 'เข้าสู่ระบบ'}
          </button>

          <p className="login-note">
            ระบบนี้มีข้อมูลส่วนบุคคลของข้าราชการ การเข้าใช้งานถือเป็นการยอมรับว่าจะใช้ข้อมูล
            เพื่อการบริหารงานบุคคลตามหน้าที่เท่านั้น
          </p>
        </form>

        <SiteFooter source={false} />
      </main>
    </div>
  );
}

/**
 * ลายพื้นหลังฝั่งซ้าย — โครงข่ายแบบผังองค์กร
 *
 * วาดด้วย SVG คงที่ ไม่ใช่การสุ่ม จะได้ไม่กระตุกตอน re-render และไม่กินซีพียู
 * ตั้ง opacity ต่ำมากโดยตั้งใจ ให้เป็นพื้นผิว ไม่ใช่ภาพประกอบที่แย่งความสนใจจากฟอร์ม
 */
function OrgLattice() {
  const cols = [
    { x: 14, ys: [50] },
    { x: 38, ys: [26, 50, 74] },
    { x: 64, ys: [14, 34, 50, 66, 86] },
    { x: 88, ys: [8, 22, 40, 50, 60, 78, 92] },
  ];

  const links: Array<[number, number, number, number]> = [];
  for (let c = 0; c < cols.length - 1; c += 1) {
    const from = cols[c] as { x: number; ys: number[] };
    const to = cols[c + 1] as { x: number; ys: number[] };
    to.ys.forEach((y, i) => {
      const parent = from.ys[Math.floor((i / to.ys.length) * from.ys.length)] as number;
      links.push([from.x, parent, to.x, y]);
    });
  }

  return (
    <svg className="lattice" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      {links.map(([x1, y1, x2, y2], i) => (
        <path key={i} d={`M${x1} ${y1} C ${(x1 + x2) / 2} ${y1}, ${(x1 + x2) / 2} ${y2}, ${x2} ${y2}`} />
      ))}
      {cols.flatMap((c) => c.ys.map((y) => <circle key={`${c.x}-${y}`} cx={c.x} cy={y} r={0.9} />))}
    </svg>
  );
}

function messageOf(err: unknown): string {
  if (err instanceof Error) {
    // ข้อความจาก request() มีรูปแบบ "เรียก /api/... ไม่สำเร็จ (401) <ข้อความจริง>"
    // ตัดส่วนหน้าที่เป็นเรื่องเทคนิคออก ให้ผู้ใช้เห็นเฉพาะข้อความที่อ่านรู้เรื่อง
    const m = /\)\s(.+)$/.exec(err.message);
    if (m?.[1]) return m[1];
    if (err.name === 'NotLoggedInError') return 'ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง';
    return err.message;
  }
  return 'เข้าสู่ระบบไม่สำเร็จ กรุณาลองใหม่';
}
